import BaseButton, { BaseButtonProps } from '../BaseButton/BaseButton';
import { useFormFocusState } from '../BaseForm/BaseForm';
import { InputHTMLAttributes, MutableRefObject, ReactNode, useMemo, useRef, useState } from 'react';
import { RefCallBack } from 'react-hook-form/dist/types';
import { BiHide, BiShow } from 'react-icons/bi';
import ReactInputMask, { BeforeMaskedStateChangeStates, InputState } from 'react-input-mask';
import { FormFeedback, FormGroup, FormGroupProps, Input, Label as ReactLabel } from 'reactstrap';
import { InputType } from 'reactstrap/types/lib/Input';
import 'scss/Main.scss';
import './BaseInput.scss';

export type MaskPropsType = {
    mask: string | Array<string | RegExp>;
    maskChar?: string | null | undefined;
    maskPlaceholder?: string | null | undefined;
    alwaysShowMask?: boolean | undefined;
    inputRef?: React.Ref<HTMLInputElement> | undefined;
    beforeMaskedStateChange?(states: BeforeMaskedStateChangeStates): InputState;
    preserveMask?: boolean;
    onAccept?: (maskedValue: string) => void;
};

export type BaseInputProps<T> = InputHTMLAttributes<HTMLInputElement> & {
    name?: keyof T & string;
    type?: InputType;
    options?: ReactNode;
    label?: string | JSX.Element;
    invalid?: boolean;
    invalidMessage?: string;
    loading?: boolean;
    isFloating?: boolean;
    innerRef?: MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
    inputClassName?: string;
    labelClassName?: string;
    rows?: number;
    buttonProps?: BaseButtonProps & Omit<BaseButtonProps, 'size' | 'color' | 'outline'>[];
    maskProps?: MaskPropsType;
    refCallback?: RefCallBack;
    formGroupProps?: Omit<FormGroupProps, 'className' | 'floating' | 'check'>;
    selecting?: boolean;
    step?: string | number | undefined;
};

const BaseInput = <T,>({
    id,
    name,
    type,
    label,
    options,
    disabled = false,
    invalid = false,
    invalidMessage,
    loading = false,
    isFloating = true,
    className,
    innerRef,
    inputClassName,
    labelClassName,
    height,
    rows,
    buttonProps,
    maskProps,
    refCallback,
    formGroupProps,
    selecting,
    value,
    ...props
}: BaseInputProps<T>) => {
    const innerClassName = 'base-input';
    const [isFocus, setFocus] = useState(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>();
    const fileInput = useRef<HTMLInputElement | null>(null);
    const [fileList, setFileList] = useState<FileList | null>();
    const formFocusContext = useFormFocusState();
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const formGroupClassName = useMemo(() => {
        let res = '';
        if (type === 'textarea' && label) {
            res += `${innerClassName}__textarea `;
            res += disabled ? `${innerClassName}__textarea_disabled` : '';
            res += loading ? `${innerClassName}__textarea_loading` : '';
        }
        return res;
    }, [type, disabled, loading]);

    const isCheckBox = type === 'checkbox' || type === 'radio';
    const isSwitch = type === 'switch';
    const floating = isFloating && !!label && !isCheckBox && !isSwitch;
    const maskChar = maskProps?.maskPlaceholder || maskProps?.maskChar || '_';

    const unmaskValue = (value: string) => {
        let unmaskedValue = '';
        for (let i = 0; i < value.length; i++) {
            const isValueChar =
                maskProps?.mask[i] === '9' ||
                maskProps?.mask[i] === 'a' ||
                maskProps?.mask[i] === '*';

            const isMaskChar = value[i] === maskChar;

            if ((isValueChar || maskProps?.preserveMask) && !isMaskChar) unmaskedValue += value[i];
        }
        return unmaskedValue;
    };

    const handleChangeMask = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = unmaskValue(event.target.value);
        maskProps?.onAccept && maskProps.onAccept(value);
        props.onChange && props.onChange(event);
    };

    const handleMaskedStateChange = (states: BeforeMaskedStateChangeStates): InputState => {
        const getValue = () => {
            if (
                states.currentState?.selection?.start === states.nextState?.selection?.start &&
                states.currentState?.selection?.end === states.nextState?.selection?.end &&
                states.currentState?.selection?.start !== 0 &&
                states.currentState?.selection?.end !== 0
            ) {
                return states.currentState;
            }

            return states.nextState ?? states.currentState;
        };

        const { value } = getValue();
        const lastPosition = value.indexOf(maskProps?.maskPlaceholder ?? '_');
        const selection = { start: lastPosition, end: lastPosition };

        return {
            value,
            selection,
        };
    };

    const inputMaskProps = maskProps && {
        tag: ReactInputMask,
        onChange: handleChangeMask,
        beforeMaskedStateChange: handleMaskedStateChange,
        ...maskProps,
    };

    const fileInputProps = useMemo(
        () =>
            type === 'file'
                ? {
                      ...props,
                      readonly: true,
                      value: fileList?.length ? fileList[0].name : '',
                      onChange: undefined,
                  }
                : props,
        [props, fileList, type]
    );

    const Label = () =>
        label ? (
            <ReactLabel
                for={id}
                className={`${labelClassName ?? ''} ${disabled ? 'disabled' : ''}`}
                check={isCheckBox}
            >
                {label}
            </ReactLabel>
        ) : (
            <></>
        );

    const rowsProp = type === 'textarea' && isFloating ? rows ?? 1 : rows;

    const inputType = useMemo(() => {
        switch (type) {
            case 'file':
                return 'text';
            case 'password':
                return showPassword ? 'text' : type;
            default:
                return type;
        }
    }, [type, showPassword]);
    return (
        <FormGroup
            className={`${className ?? ''} ${innerClassName} ${formGroupClassName}`}
            floating={floating}
            check={isCheckBox}
            switch={isSwitch}
            onClick={(e) => {
                if (!isCheckBox) inputRef.current?.focus();
                formGroupProps?.onClick && formGroupProps?.onClick(e);
            }}
            {...formGroupProps}
        >
            {!floating && <Label />}
            {type === 'file' && (
                <input
                    hidden
                    type="file"
                    ref={fileInput}
                    onChange={(e) => {
                        if (e.target.files?.length) {
                            setFileList(e.target.files);
                        }

                        if (props.onChange) {
                            props.onChange(e);
                        }
                    }}
                />
            )}
            {buttonProps && (
                <div className="buttons_group">
                    {buttonProps.map((item, ids) => (
                        <BaseButton {...item} key={ids} outline={true} color="link" size="sm">
                            {item.children}
                        </BaseButton>
                    ))}
                </div>
            )}
            <Input
                id={id}
                invalid={invalid}
                type={inputType}
                rows={rowsProp}
                value={value === null ? '' : value}
                name={name}
                onClick={(e) => {
                    e.stopPropagation();
                    if (fileInput && fileInput.current) {
                        fileInput.current.click();
                    }
                }}
                step={type === 'number' ? props.step ?? 'any' : undefined}
                placeholder={typeof label === 'string' ? label : undefined}
                disabled={disabled || loading}
                onFocus={(e) => {
                    setFocus(true);
                    props.onFocus && props.onFocus(e);
                }}
                onBlur={(e) => {
                    setFocus(false);
                    props.onBlur && props.onBlur(e);
                }}
                className={`custom-scrollbar ${loading ? `loading` : ''} ${
                    inputClassName ? inputClassName : ''
                } ${selecting === false ? 'unselect' : ''}`}
                onMouseDown={(e) => {
                    if (selecting === false) {
                        e.preventDefault();
                    }
                    props.onMouseDown && props.onMouseDown(e);
                }}
                innerRef={(instance) => {
                    refCallback &&
                        refCallback({
                            ...instance,
                            focus: () => {
                                formFocusContext.setFocusFieldRef(instance);
                                setTimeout(() => instance?.focus(), 1);
                            },
                        });

                    inputRef.current = instance;
                    if (innerRef) innerRef.current = instance;
                }}
                style={height ? { minHeight: height } : undefined}
                {...fileInputProps}
                {...inputMaskProps}
            >
                {options}
            </Input>
            {type === 'password' && (
                <div
                    className="toggle-password-button cursor--pointer"
                    onClick={togglePasswordVisibility}
                >
                    {showPassword ? <BiShow /> : <BiHide />}
                </div>
            )}
            {/*{type === 'password' && (showPassword ? <BiShow /> : <BiHide />)}*/}
            {floating && <Label />}
            {isFocus && invalidMessage && (
                <FormFeedback tooltip>{invalid && invalidMessage}</FormFeedback>
            )}
        </FormGroup>
    );
};

export default BaseInput;
